/**
 * ตรรกะของหน้าจอเคส NCR ที่แยกออกจาก React — ฟังก์ชันล้วน ทดสอบได้โดยไม่ต้อง render
 * (ฝาแฝดของ `lib/quality/qualityRulesView.ts` และ `lib/outsourcing/outsourcingView.ts`)
 *
 * ไฟล์นี้ถือ **THE CONSEQUENCE HEADLINE** (D-108 ข้อ 2, invariant ของ WO นี้):
 * เลือก disposition แล้วต้องเห็นว่าจะกระทบล็อตไหนและล็อตนั้นจะไปอยู่สถานะอะไร
 * **ก่อน** กดยืนยัน — `closeConsequencePreview` คือฟังก์ชันที่ต้องพิสูจน์ข้อนี้ และ
 * `NcrCaseScreen.test.tsx` เป็นผู้พิสูจน์ว่าการถอดการเรียกมันออกจากหน้าจอทำให้เทสต์
 * แดง (mutation-checkable)
 */

import {
  DISPOSITION_LOT_STATUS,
  NCR_DISPOSITIONS,
  NCR_STATUSES,
  type NcrCase,
  type NcrDisposition,
  type NcrReferenceDocType,
  type NcrStatus,
} from './types';

/** ฝาแฝดของ `StatusTone` ใน `desktop/StatusBadge.tsx` — restated แทนการ import
 * ข้าม layer (lib ไม่พึ่ง desktop) เหตุผลเดียวกับ `lib/outsourcing/outsourcingView.ts` */
export type NcrStatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'planned';

/* --------------------------------------------------------------- สถานะ --- */

export function ncrStatusLabel(status: string): string {
  switch (status) {
    case 'OPEN':
      return 'เปิดใหม่';
    case 'UNDER_REVIEW':
      return 'กำลังสอบสวน';
    case 'CLOSED':
      return 'ปิดแล้ว';
    default:
      return status;
  }
}

export function ncrStatusTone(status: string): NcrStatusTone {
  switch (status) {
    case 'OPEN':
      return 'warning';
    case 'UNDER_REVIEW':
      return 'info';
    case 'CLOSED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function isNcrStatus(value: string): value is NcrStatus {
  return (NCR_STATUSES as readonly string[]).includes(value);
}

/* -------------------------------------------------------------- อ้างอิง -- */

export function ncrReferenceTypeLabel(type: string): string {
  switch (type) {
    case 'LOT':
      return 'ล็อต (Lot)';
    case 'GOODS_RECEIPT':
      return 'ใบรับเข้า (Goods Receipt)';
    case 'OPERATION_CONFIRMATION':
      return 'ใบยืนยันงาน (Operation Confirmation)';
    default:
      return type;
  }
}

export function isNcrReferenceDocType(value: string): value is NcrReferenceDocType {
  return value === 'LOT' || value === 'GOODS_RECEIPT' || value === 'OPERATION_CONFIRMATION';
}

/* ---------------------------------------------------------- disposition -- */

/**
 * คำอธิบายของ disposition แต่ละแบบ ที่ต้องพูดถึง **ผลต่อล็อต** ให้ชัด — เฉพาะเจาะจง
 * ที่สุดสำหรับ `SCRAP` เพราะมันคือแบบเดียวที่ทำลายวัสดุ (invariant ของ WO นี้:
 * "ห้ามแสดง SCRAP เป็นตัวเลือกหนึ่งในสี่ที่น้ำหนักเท่ากัน")
 *
 * เรียงลำดับ **ไม่ใช่ตามตัวอักษร** แต่ตามความหนักของผล: ปล่อยผ่าน → พักไว้แก้ →
 * ส่งคืนผู้ขาย → ทำลาย — SCRAP อยู่ท้ายสุดและมี tone `danger` เป็นตัวเดียว
 */
export interface DispositionDescriptor {
  readonly value: NcrDisposition;
  readonly label: string;
  /** สิ่งที่เกิดกับล็อต — ประโยคที่ต้องอ่านแล้วรู้ผลทันทีโดยไม่ต้องกดอะไรต่อ */
  readonly effect: string;
  readonly tone: NcrStatusTone;
  readonly destructive: boolean;
}

export const DISPOSITION_DESCRIPTORS: readonly DispositionDescriptor[] = [
  {
    value: 'USE_AS_IS',
    label: 'ใช้ได้ตามสภาพ (Use As-Is)',
    effect: 'ปล่อยล็อตออกจากการกักกัน — กลับไปใช้งานได้ตามปกติ',
    tone: 'success',
    destructive: false,
  },
  {
    value: 'REWORK',
    label: 'ส่งแก้ไข (Rework)',
    effect: 'พักล็อตไว้ไม่ให้ใช้งาน จนกว่าจะแก้ไขแล้วมีคนตัดสินใจใหม่อีกครั้ง',
    tone: 'warning',
    destructive: false,
  },
  {
    value: 'RETURN_TO_SUPPLIER',
    label: 'ส่งคืนผู้ขาย (Return to Supplier)',
    effect: 'ปฏิเสธล็อตนี้ — กันไว้ไม่ให้ใช้งานเพื่อส่งคืน (การส่งคืนจริงเป็นเอกสารแยกต่างหาก)',
    tone: 'warning',
    destructive: false,
  },
  {
    value: 'SCRAP',
    label: 'ทำลายทิ้ง (Scrap)',
    effect:
      'ปฏิเสธล็อตนี้อย่างถาวร — วัสดุนี้จะไม่ถูกใช้งานอีก (การตัดบัญชีมูลค่าเป็นเอกสารแยกต่างหาก และล็อตยังคงอยู่ในระบบเพื่อการตามสอบ)',
    tone: 'danger',
    destructive: true,
  },
];

/** ต้องตรงกับ `DISPOSITION_LOT_STATUS` เสมอ — mutation-checkable */
export function targetLotStatusForDisposition(disposition: NcrDisposition): string {
  return DISPOSITION_LOT_STATUS[disposition];
}

export function lotStatusLabel(status: string): string {
  switch (status) {
    case 'RELEASED':
      return 'ปล่อยใช้งานได้ (RELEASED)';
    case 'BLOCKED':
      return 'ถูกพักไว้ (BLOCKED)';
    case 'REJECTED':
      return 'ถูกปฏิเสธถาวร (REJECTED)';
    default:
      return status;
  }
}

/**
 * **THE CONSEQUENCE HEADLINE.** ผลของการปิดเคสด้วย disposition หนึ่ง ก่อนกดยืนยัน
 * — คืน `null` เมื่อยังไม่ได้เลือก disposition เลย (ยังไม่มีอะไรให้ทำนาย)
 *
 * เฉพาะเคสที่อ้างถึง `LOT` เท่านั้นที่ `movesLot` เป็น `true` — เคสที่อ้างถึงใบรับเข้า
 * หรือใบยืนยันงานไม่ย้ายล็อตใดเลย (`modules/quality/service.py close_ncr_case`)
 * และหน้าจอต้องพูดความจริงข้อนี้ ไม่ใช่แสดงคำทำนายสถานะให้เคสที่ไม่มีล็อตให้ย้าย
 */
export interface CloseConsequencePreview {
  readonly movesLot: boolean;
  readonly referenceDocType: string;
  readonly referenceDocId: string;
  readonly targetStatus: string | null;
}

export function closeConsequencePreview(
  ncrCase: NcrCase,
  disposition: NcrDisposition | '',
): CloseConsequencePreview | null {
  if (disposition === '') {
    return null;
  }
  const movesLot = ncrCase.reference_doc_type === 'LOT';
  return {
    movesLot,
    referenceDocType: ncrCase.reference_doc_type,
    referenceDocId: ncrCase.reference_doc_id,
    targetStatus: movesLot ? targetLotStatusForDisposition(disposition) : null,
  };
}

export function isNcrDisposition(value: string): value is NcrDisposition {
  return (NCR_DISPOSITIONS as readonly string[]).includes(value);
}

/* --------------------------------------------------------- ฟอร์มเปิดเคส -- */

export interface CreateNcrCaseFields {
  readonly plantCode: string;
  readonly referenceDocType: string;
  readonly referenceDocId: string;
  readonly defectDescription: string;
  readonly investigationNote: string;
}

export const EMPTY_CREATE_NCR_CASE_FIELDS: CreateNcrCaseFields = {
  plantCode: '',
  referenceDocType: '',
  referenceDocId: '',
  defectDescription: '',
  investigationNote: '',
};

/**
 * ประกอบ body ของ `POST /v1/ncr-cases` — คืน `null` เมื่อฟอร์มยังกรอกไม่ครบ
 * (ปุ่มยืนยันผูกกับผลนี้โดยตรง จึงกดไม่ได้จนกว่าจะครบ)
 */
export function toCreateNcrCaseBody(
  fields: CreateNcrCaseFields,
): { readonly plant_code: string; readonly reference_doc_type: NcrReferenceDocType; readonly reference_doc_id: string; readonly defect_description: string; readonly investigation_note?: string } | null {
  const plantCode = fields.plantCode.trim();
  const referenceDocType = fields.referenceDocType.trim();
  const referenceDocId = fields.referenceDocId.trim();
  const defectDescription = fields.defectDescription.trim();
  const investigationNote = fields.investigationNote.trim();

  if (
    plantCode === '' ||
    referenceDocId === '' ||
    defectDescription === '' ||
    !isNcrReferenceDocType(referenceDocType)
  ) {
    return null;
  }

  return {
    plant_code: plantCode,
    reference_doc_type: referenceDocType,
    reference_doc_id: referenceDocId,
    defect_description: defectDescription,
    ...(investigationNote === '' ? {} : { investigation_note: investigationNote }),
  };
}

/* ------------------------------------------------------- ฟอร์มสอบสวน --- */

/**
 * ประกอบ body ของ `POST .../investigate` — `investigation_note` บังคับที่นี่
 * (ต่างจากตอนเปิดเคส) เพราะย้ายเคสไปสอบสวนโดยไม่เขียนว่ากำลังสอบอะไรคือการบันทึก
 * การสอบสวนที่ไม่มีใครอ่านรู้เรื่อง
 */
export function toInvestigateNcrCaseBody(
  note: string,
): { readonly investigation_note: string } | null {
  const trimmed = note.trim();
  return trimmed === '' ? null : { investigation_note: trimmed };
}

/* ----------------------------------------------------------- ฟอร์มปิดเคส -- */

/** ประกอบ body ของ `POST .../close` — คืน `null` จนกว่าจะเลือก disposition */
export function toCloseNcrCaseBody(
  disposition: NcrDisposition | '',
  note: string,
): { readonly disposition: NcrDisposition; readonly investigation_note?: string } | null {
  if (disposition === '') {
    return null;
  }
  const trimmed = note.trim();
  return {
    disposition,
    ...(trimmed === '' ? {} : { investigation_note: trimmed }),
  };
}

/* -------------------------------------------------------------- ตัวกรอง -- */

export function toNcrPlantCodeFilter(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

export function toNcrStatusFilter(raw: string): string | null {
  return isNcrStatus(raw) ? raw : null;
}
